/// <amd-module name="Build/Admin/PoolsList/Components/CreatePoolPanel" />
import * as React from "react";

import { TaskAgentPool } from "DistributedTask/Client/Contracts/TaskAgent";

import { IProjectData } from "Tfs/Platform/Page";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPageService, IPageData } from "VSS/Platform/Page";

import { IButtonProps } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Dropdown } from "VSSUI/Dropdown";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup, RadioButtonGroupDirection } from "VSSUI/RadioButton";

import { PoolType } from "Build/Admin/Common/Client/Contracts/DistributedTask/DataProvider";
import { FwLinks } from "Build/Admin/Common/Constants";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolsListActionCreator } from "Build/Admin/PoolsList/Actions/PoolsListActionCreator";
import { CreateElasticPoolContainer } from "Build/Admin/PoolsList/Components/CreateElasticPoolContainer";
import { CreateExistingPoolContainer } from "Build/Admin/PoolsList/Components/CreateExistingPoolContainer";
import { CreateNewPoolContainer } from "Build/Admin/PoolsList/Components/CreateNewPoolContainer";
import { StoresHub } from "Build/Admin/PoolsList/Stores/StoresHub";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import * as Resources from "Build/Admin/PoolsList/Resources";

export interface ICreatePoolPanelProps {
    actionCreator: PoolsListActionCreator;
    storesHub: StoresHub;
}

interface ICreatePoolPanelState {
    isOpen: boolean;
    newOrExistingRadioButton: string;
    existingPoolOptions: IListBoxItem<TaskAgentPool>[];
    availablePoolTypes: IListBoxItem<PoolType>[];
    selectedPoolType?: PoolType;
    footerButtonProps: IButtonProps[] | undefined;
}

const useExistingRadioId: string = "pipelines-create-pool-existing-radio";
const createNewRadioId: string = "pipelines-create-pool-new-radio";

const poolTypeToName: { [key: number]: string } = {
    [PoolType.Private]: Resources.PrivatePoolType,
    [PoolType.Elastic]: Resources.ElasticPoolType,
    [PoolType.AgentCloud]: Resources.OtherAgentCloudPoolType,
};

export class CreatePoolPanel extends VssComponent<ICreatePoolPanelProps, ICreatePoolPanelState> {
    private _onPrem: boolean;
    private _project: IProjectData | undefined;
    private _disableAllPipelinesSetting: boolean;

    constructor(props: ICreatePoolPanelProps, context: IVssComponentContext) {
        super(props, context);
        this.state = this._getStateFromStores();

        const vssPageService: IVssPageService = context.pageContext.getService<IVssPageService>("IVssPageService");
        const projectDataService: IProjectDataService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        const vssPageData: IPageData | undefined = vssPageService.getData();

        if (vssPageData) {
            this._onPrem = !vssPageData.isHosted;
        }

        this._project = projectDataService.getProjectData();
        
        this._disableAllPipelinesSetting = !this.props.storesHub.securityStore.state.hasManageAllPermission;
    }

    public componentDidMount(): void {
        this.props.storesHub.createPoolPanelStore.addChangedListener(this._onStoreChanged);
    }

    public componentWillUnmount(): void {
        this.props.storesHub.createPoolPanelStore.removeChangedListener(this._onStoreChanged);
    }

    public render(): JSX.Element | null {
        return (
            <ConditionalChildren renderChildren={this.state.isOpen}>
                <Panel
                    className="pipelines-create-pool-panel"
                    titleProps={{ text: Resources.CreateNewPoolTitle }}
                    description={this._getDescription()}
                    onDismiss={this._cancelOnClick}
                    footerButtonProps={this.state.footerButtonProps}>
                    <div className="padding-top-8 flex-column flex-grow">
                        <ConditionalChildren renderChildren={this._project !== undefined}>
                            <RadioButtonGroup
                                className="new-existing-radio-buttons"
                                text={Resources.PoolToLink}
                                direction={RadioButtonGroupDirection.Horizontal}
                                selectedButtonId={this.state.newOrExistingRadioButton}
                                onSelect={(newOrExistingRadioButton) => this.setState({ newOrExistingRadioButton })} >
                                <RadioButton id={createNewRadioId} text={Resources.UseNewPoolLabel} />
                                <RadioButton
                                    id={useExistingRadioId}
                                    text={Resources.UseExistingPoolLabel}
                                    disabled={this.state.existingPoolOptions.length === 0}
                                    tooltipProps={{
                                        text: Resources.NoExistingPoolToolTip,
                                        disabled: this.state.existingPoolOptions.length !== 0
                                    }} />
                            </RadioButtonGroup>
                        </ConditionalChildren>
                        {this._getPanelBody()}
                    </div>
                </Panel>
            </ConditionalChildren>
        );
    }

    private _getPanelBody(): JSX.Element | null {
        if (this.state.newOrExistingRadioButton === useExistingRadioId) {
            return <CreateExistingPoolContainer
                disableAllPipelinesSetting={this._disableAllPipelinesSetting}
                actionCreator={this.props.actionCreator}
                existingPoolOptions={this.state.existingPoolOptions}
                onFooterButtonPropsChanged={this._onFooterButtonPropsChanged} />;
        }
        else if (this.state.newOrExistingRadioButton === createNewRadioId) {
            return (
                <div className="new-pool-section">
                    <ConditionalChildren renderChildren={this.state.availablePoolTypes.length > 1}>
                        <div className={this._project !== undefined ? "pool-setting-label" : "padding-bottom-8"}>
                            <span className="margin-right-8">{Resources.PoolTypeLabel}</span>
                        </div>
                        <Dropdown
                            className="pool-type-dropdown"
                            placeholder={Resources.SelectAgentPoolType}
                            items={this.state.availablePoolTypes}
                            onSelect={this._onPoolTypeSelectionChanged} />
                        <ConditionalChildren renderChildren={this.state.selectedPoolType !== undefined}>
                            {this._getPoolTypeDescription()}
                        </ConditionalChildren>
                    </ConditionalChildren>
                    {this._getPoolTypeSettingsBody()}
                </div>
            );
        }
        else {
            return null;
        }
    }

    private _getPoolTypeDescription(): JSX.Element | undefined {
        switch (this.state.selectedPoolType) {
            case PoolType.Private:
                return (
                    <div className="padding-top-8">
                        {Resources.PrivatePoolDescription}
                        <Link className="margin-left-4" href={FwLinks.PrivatePoolInstructions} target="_blank">{Resources.LearnMore}</Link>
                    </div>
                );

            case PoolType.Elastic:
                return (
                    <div className="padding-top-8">
                        {Resources.ElasticPoolDescription}
                        <Link className="margin-left-4" href={FwLinks.ElasticPoolInstructions} target="_blank">{Resources.ElasticPoolLearnMore}</Link>
                    </div>
                );

            case PoolType.AgentCloud:
            default:
                return undefined;
        }
    }

    private _getPoolTypeSettingsBody(): JSX.Element | null {
        switch (this.state.selectedPoolType) {
            case PoolType.Private:
            case PoolType.AgentCloud:
                return <CreateNewPoolContainer
                    actionCreator={this.props.actionCreator}
                    storesHub={this.props.storesHub}
                    showAgentCloudDropdown={this.state.selectedPoolType === PoolType.AgentCloud}
                    onFooterButtonPropsChanged={this._onFooterButtonPropsChanged} />;

            case PoolType.Elastic:
                return <CreateElasticPoolContainer
                    actionCreator={this.props.actionCreator}
                    storesHub={this.props.storesHub}
                    onFooterButtonPropsChanged={this._onFooterButtonPropsChanged} />;

            default:
                return null;
        }
    }

    private _getDescription = (): string => {
        if (this._onPrem) {
            return Resources.CreateNewPoolDescriptionOnPrem;
        }
        else {
            return Resources.CreateNewPoolDescriptionHosted;
        }
    }

    private _onPoolTypeSelectionChanged = (event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<PoolType>): void => {
        this.setState({
            selectedPoolType: item.data
        });
    }

    private _onFooterButtonPropsChanged = (footerButtonProps: IButtonProps[]): void => {
        this.setState({
            footerButtonProps
        });
    }

    private _cancelOnClick = (): void => {
        this.props.actionCreator.dismissCreatePoolPanel();
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores = (): ICreatePoolPanelState => {
        // This is used on panel initialization, and to reset the panel state on open/close
        // so some of the panel's fields are set to their default values below.
        // The panel's fields are set by setState calls in the child component callbacks in the render function.
        const availablePoolTypes: IListBoxItem<PoolType>[] = this._getAvailablePoolTypeOptions();
        return {
            isOpen: this.props.storesHub.createPoolPanelStore.state.isOpen,
            newOrExistingRadioButton: createNewRadioId,
            existingPoolOptions: this._getExistingPoolOptions(),
            availablePoolTypes,
            selectedPoolType: availablePoolTypes.length === 1 ? availablePoolTypes[0].data : undefined,
            footerButtonProps: undefined
        };
    }

    private _getExistingPoolOptions = (): IListBoxItem<TaskAgentPool>[] => {
        const poolOptions: IListBoxItem<TaskAgentPool>[] = [];

        if (this._project) {
            const inUsePoolsMap: { [key: number]: boolean } = {};
            this.props.storesHub.agentQueueStore.state.forEach(queue => {
                if (!inUsePoolsMap[queue.pool.id]) {
                    inUsePoolsMap[queue.pool.id] = true;
                }
            });

            this.props.storesHub.agentPoolStore.state.forEach(pool => {
                if (!inUsePoolsMap[pool.id]) {
                    poolOptions.push({
                        id: pool.id.toString(),
                        text: pool.name,
                        data: pool
                    });
                }
            });
        }

        return poolOptions;
    }

    private _getAvailablePoolTypeOptions(): IListBoxItem<PoolType>[] {
        return this.props.storesHub.createPoolPanelStore.state.availablePoolTypes.map(pt => {
            return {
                id: poolTypeToName[pt],
                text: poolTypeToName[pt],
                data: pt
            } as IListBoxItem<PoolType>;
        });
    }
}
